/**
 * NewRelic logging helpers for application monitoring
 *
 * Provides a clean interface for logging custom PageAction events to NewRelic
 * without cluttering business logic with conditional checks.
 *
 * Supports authentication flow monitoring and order processing tracking.
 */

/**
 * Logs a custom event to NewRelic if available
 * @param {string} actionName - The name of the custom action
 * @param {object} attributes - Key-value pairs of additional data
 */
const logPageAction = (actionName, attributes = {}) => {
  if (window.newrelic && typeof window.newrelic.addPageAction === "function") {
    try {
      window.newrelic.addPageAction(actionName, attributes);
    } catch (error) {
      // Silently fail - don't let NewRelic errors break the app
      console.warn("Failed to log NewRelic PageAction:", error);
    }
  }
};

/**
 * Authentication-specific NewRelic logging helpers
 */
export const authMetrics = {
  /**
   * Log when a request is queued during token refresh
   * @param {string} url - The URL that was queued
   * @param {number} queueLength - Current queue length
   */
  logRefreshQueued: (url, queueLength) => {
    logPageAction("auth_token_refresh_queued", { url, queueLength });
  },

  /**
   * Log successful token refresh
   * @param {string} originalUrl - The URL that triggered the refresh
   * @param {number} queuedRequests - Number of requests that were waiting
   */
  logRefreshSuccess: (originalUrl, queuedRequests) => {
    logPageAction("auth_token_refresh_success", {
      originalUrl,
      queuedRequests,
    });
  },

  /**
   * Log failed token refresh
   * @param {string} originalUrl - The URL that triggered the refresh
   * @param {number} queuedRequests - Number of requests that failed
   * @param {string} errorMessage - Error details
   */
  logRefreshFailed: (originalUrl, queuedRequests, errorMessage) => {
    logPageAction("auth_token_refresh_failed", {
      originalUrl,
      queuedRequests,
      errorMessage,
    });
  },

  /**
   * Log circuit breaker trigger (emergency stop)
   * @param {number} requestCount - Number of requests that triggered the breaker
   */
  logCircuitBreaker: (requestCount) => {
    logPageAction("auth_circuit_breaker_triggered", {
      requestCount,
      timestamp: new Date().toISOString(),
    });
  },
};

/**
 * Order processing and e-commerce NewRelic logging helpers
 */
export const orderMetrics = {
  /**
   * Log when a cart is created
   * @param {string} sessionId - Unique session identifier for correlation
   * @param {string} source - Where the cart was created (e.g., 'product_page', 'care_flow')
   */
  logCartCreated: (sessionId, source) => {
    logPageAction("order_cart_created", {
      sessionId,
      source,
      timestamp: new Date().toISOString(),
    });
  },

  /**
   * Log when a cart response arrived without its line_items array (DVY-4969).
   * A well-formed API cart always includes line_items, so this fires only on the
   * defensive fallback: lets us see if the transient recurs and whether the
   * affected carts were expected to be empty vs. dropped items.
   * @param {string} cartId - The cart that was missing line_items
   * @param {string} status - The cart status
   */
  logCartMissingLineItems: (cartId, status) => {
    logPageAction("order_cart_missing_line_items", {
      cartId,
      status,
    });
  },

  /**
   * Log when an item is added to cart
   * @param {string} sessionId - Session identifier
   * @param {string} productSku - Product SKU added
   * @param {number} quantity - Quantity added
   * @param {number} cartValue - Total cart value after addition
   * @param {number} itemCount - Total items in cart
   */
  logCartItemAdded: (sessionId, productSku, quantity, cartValue, itemCount) => {
    logPageAction("order_cart_item_added", {
      sessionId,
      productSku,
      quantity,
      cartValue,
      itemCount,
    });
  },

  /**
   * Log when an item is removed from cart
   * @param {string} sessionId - Session identifier
   * @param {string} productSku - Product SKU removed
   * @param {number} quantity - Quantity removed
   * @param {number} cartValue - Total cart value after removal
   * @param {number} itemCount - Total items in cart
   */
  logCartItemRemoved: (
    sessionId,
    productSku,
    quantity,
    cartValue,
    itemCount
  ) => {
    logPageAction("order_cart_item_removed", {
      sessionId,
      productSku,
      quantity,
      cartValue,
      itemCount,
    });
  },

  /**
   * Log when checkout process is started
   * @param {string} sessionId - Session identifier
   * @param {number} cartValue - Total cart value
   * @param {number} itemCount - Number of items in cart
   * @param {string} checkoutSource - Source of checkout (e.g., 'cart_page', 'care_flow')
   */
  logCheckoutStarted: (sessionId, cartValue, itemCount, checkoutSource) => {
    logPageAction("order_checkout_started", {
      sessionId,
      cartValue,
      itemCount,
      checkoutSource,
    });
  },

  /**
   * Log when checkout is completed successfully
   * @param {string} sessionId - Session identifier
   * @param {string} orderId - Order ID from backend
   * @param {string} orderNumber - Order number from Shopify
   * @param {number} orderValue - Total order value
   * @param {string} paymentMethod - Payment method used
   */
  logCheckoutCompleted: (
    sessionId,
    orderId,
    orderNumber,
    orderValue,
    paymentMethod
  ) => {
    logPageAction("order_checkout_completed", {
      sessionId,
      orderId,
      orderNumber,
      orderValue,
      paymentMethod,
    });
  },

  /**
   * Log when checkout is abandoned
   * @param {string} sessionId - Session identifier
   * @param {string} abandonmentStage - Stage where user abandoned (e.g., 'shipping', 'payment')
   * @param {number} cartValue - Cart value at abandonment
   * @param {string} errorMessage - Error message if abandonment was due to error
   */
  logCheckoutAbandoned: (
    sessionId,
    abandonmentStage,
    cartValue,
    errorMessage = null
  ) => {
    logPageAction("order_checkout_abandoned", {
      sessionId,
      abandonmentStage,
      cartValue,
      errorMessage,
    });
  },

  /**
   * Log checkout errors
   * @param {string} sessionId - Session identifier
   * @param {string} errorType - Type of error (e.g., 'payment_failed', 'validation_error')
   * @param {string} errorMessage - Detailed error message
   * @param {string} checkoutStep - Step where error occurred
   */
  logCheckoutError: (sessionId, errorType, errorMessage, checkoutStep) => {
    logPageAction("order_checkout_error", {
      sessionId,
      errorType,
      errorMessage,
      checkoutStep,
    });
  },

  /**
   * Log order status page views
   * @param {string} orderId - Order ID
   * @param {string} orderNumber - Order number
   * @param {string} orderStatus - Current order status
   */
  logOrderStatusViewed: (orderId, orderNumber, orderStatus) => {
    logPageAction("order_status_viewed", {
      orderId,
      orderNumber,
      orderStatus,
    });
  },

  /**
   * Generate a unique session ID for correlation
   * @returns {string} Unique session identifier
   */
  generateSessionId: () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  },
};

// Generic export for other use cases
export { logPageAction };
